import { ArrowRight, Phone, CheckCircle2 } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/441242bb-e7c4-453a-9f7c-5eb3d41b77a0/images/561c2474c4c48a26b1466cc43796a1eabbb2956c4aaaacc10e0fc293518a1960.png";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28"
    >
      {/* Background decor */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle_at_center,hsl(var(--brand-terra)/0.08),transparent_70%)]" />
        <div className="absolute left-0 bottom-0 h-px w-full bg-border" />
      </div>

      <div className="container-wide relative grid gap-16 md:grid-cols-12 md:gap-10 items-center">
        {/* Left: content */}
        <div className="md:col-span-7 reveal">
          <div className="flex items-center gap-3 mb-8">
            <span className="rule" />
            <span className="overline">La Réunion · Bureau d'études & permis de construire</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-[4.5rem] font-medium leading-[1.05] text-primary">
            Construisez à{" "}
            <span className="italic font-light text-[hsl(var(--brand-terra))]">
              La Réunion
            </span>{" "}
            sans mauvaise surprise.
          </h1>

          <p className="mt-6 max-w-xl text-lg sm:text-xl font-light text-primary/85 leading-snug">
            Étude de sol, permis de construire et accompagnement complet —
            piloté par un seul interlocuteur pour vous éviter{" "}
            <span className="text-[hsl(var(--brand-terra))] font-medium">
              les refus, les retards et les surcoûts
            </span>
            .
          </p>

          <p className="mt-7 max-w-lg text-base leading-relaxed text-muted-foreground">
            Études de sol G1 à G5, dossiers de permis et pilotage de projet,
            géré de A à Z par notre équipe locale, en partenariat avec{" "}
            <span className="text-[hsl(var(--brand-terra))] font-medium">Innocean</span>.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-devis"
              className="btn-primary group"
            >
              Demander mon devis gratuit
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
            <a
              href="#callback"
              data-testid="hero-cta-rappel"
              className="btn-outline group"
            >
              <Phone size={16} strokeWidth={1.6} />
              Être rappelé rapidement
            </a>
          </div>

          <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={13} strokeWidth={2} className="text-[hsl(var(--brand-terra))]" />
              Devis gratuit et sans engagement
            </span>
            <span className="text-border">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="text-[hsl(var(--brand-terra))]">⚡</span>
              Réponse rapide sous 24h
            </span>
          </p>

          {/* Micro-preuves bar — 4 reassurance signals */}
          <div
            data-testid="hero-microproofs"
            className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2.5 text-[12px] text-primary/85 font-medium"
          >
            {[
              "+100 projets réalisés",
              "Toute l'île de La Réunion",
              "Réponse sous 24h",
              "Devis gratuit & sans engagement",
            ].map((label, i) => (
              <span key={label} className="inline-flex items-center gap-2">
                {i > 0 && (
                  <span aria-hidden className="hidden sm:inline text-border">
                    ·
                  </span>
                )}
                <CheckCircle2
                  size={14}
                  strokeWidth={2}
                  className="text-[hsl(var(--brand-terra))] shrink-0"
                />
                {label}
              </span>
            ))}
          </div>

          {/* Trust strip */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-2xl">
            {[
              "+100 projets réalisés",
              "Expertise à La Réunion",
              "Accompagnement complet",
            ].map((t) => (
              <div key={t} className="flex items-start gap-2.5">
                <CheckCircle2
                  size={18}
                  strokeWidth={1.5}
                  className="text-[hsl(var(--brand-terra))] mt-0.5 shrink-0"
                />
                <span className="text-sm text-primary/85 font-medium">{t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: image */}
        <div className="md:col-span-5 reveal" data-testid="hero-visual">
          <div className="relative">
            <div className="absolute -top-4 -left-4 h-24 w-24 border-t border-l border-[hsl(var(--brand-terra))]" />
            <div className="absolute -bottom-4 -right-4 h-24 w-24 border-b border-r border-[hsl(var(--brand-terra))]" />
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
              <img
                src={HERO_IMG}
                alt="Esquisse architecturale d'une maison moderne à La Réunion"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/40 to-transparent">
                <div className="inline-flex items-center gap-2 bg-white/95 px-3 py-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-terra))]" />
                  <span className="text-xs tracking-[0.16em] uppercase text-primary font-semibold">
                    Clé en main
                  </span>
                </div>
              </div>
            </div>
            {/* Stat badge */}
            <div className="absolute -left-6 top-10 hidden md:flex flex-col items-start bg-white border border-border p-5 shadow-[0_20px_60px_-20px_rgba(47,45,42,0.22)]">
              <span className="font-display text-4xl leading-none text-primary">
                +100
              </span>
              <span className="mt-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Projets réalisés
              </span>
              <span className="mt-3 block h-0.5 w-10 bg-[hsl(var(--brand-terra))]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
